import { DataScriptApprovalResponse } from "@/models/small-data-fix";
import { PageScriptActionsService } from "@/services/PageScriptActionsService";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const DataScriptApproval = () => {
  const [isAlreadyApproved, setApproval] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Read params from route:
  // /DataScriptApproval/:incidentNumber
  // /DataScriptApproval/:incidentNumber/:approvalTarget
  const { incidentNumber: rawIncidentNumber, approvalTarget: rawApprovalTarget } = useParams<{
    incidentNumber: string;
    approvalTarget?: string;
  }>();

  const incidentNumber = rawIncidentNumber?.toUpperCase();
  const approvalTarget = rawApprovalTarget?.trim().toLowerCase() || null;

  const fetchData = useCallback(async () => {
    try {
      if (!incidentNumber) {
        setError('Missing required parameter: incidentNumber');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      const isPaymentPortalExists = approvalTarget?.includes('paymentportal') ? true : false;
      const response =
        await PageScriptActionsService.GetPrePreviewByIncident<DataScriptApprovalResponse>(
          incidentNumber,
          false,
          isPaymentPortalExists
        );

      if (response?.id == 0) {
        throw new Error('Failed to fetch data');
      }


      if (response.isApproved) {
        setApproval(true);
      } else {
        await PageScriptActionsService.approveSript({
          SNOWIncidentNumber: incidentNumber,
          isApproved: true,
          IsPaymentPortal: isPaymentPortalExists ?? false
        });
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message || 'Unexpected error');
      } else {
        setError('Unexpected error');
      }
    } finally {
      setLoading(false);
    }
  }, [incidentNumber, approvalTarget]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const styles: Record<string, React.CSSProperties> = {
    page: {
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f4f6f8',
      padding: 24,
      fontFamily: 'Segoe UI, Roboto, Arial, sans-serif',
    },

    card: {
      width: '100%',
      maxWidth: 520,
      backgroundColor: '#ffffff',
      borderRadius: 8,
      padding: '32px 28px',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
      textAlign: 'center',
    },

    title: {
      marginBottom: 24,
      fontSize: 22,
      fontWeight: 600,
      color: '#1f2933',
    },

    infoBox: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 12,
      color: '#374151',
    },

    infoText: {
      fontSize: 14,
      margin: 0,
    },

    spinner: {
      width: 18,
      height: 18,
      border: '3px solid #d1d5db',
      borderTop: '3px solid #2563eb',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    },

    messageBox: {
      marginTop: 12,
      padding: '16px 18px',
      borderRadius: 6,
      fontSize: 14,
    },

    existing: {
      backgroundColor: '#fffbeb',
      color: '#92400e',
      border: '1px solid #fde68a',
    },

    success: {
      backgroundColor: '#ecfdf5',
      color: '#065f46',
      border: '1px solid #a7f3d0',
    },

    error: {
      backgroundColor: '#fef2f2',
      color: '#991b1b',
      border: '1px solid #fecaca',
    },

    pre: {
      margin: 0,
      whiteSpace: 'pre-wrap',
      fontFamily: 'inherit',
    },
  };

  return (
    <div style={styles.page}>
      <section style={styles.card}>
        <h1 style={styles.title}>Data Script Approval</h1>

        {loading && (
          <div style={styles.infoBox}>
            <span style={styles.spinner} />
            <p style={styles.infoText}>Processing your request...</p>
          </div>
        )}

        {!loading && error && (
          <div style={{ ...styles.messageBox, ...styles.error }}>
            <p>Sorry, unfortunately an error occurred, please re-validate</p>
          </div>
        )}

        {!loading && !error && isAlreadyApproved && (
          <div style={{ ...styles.messageBox, ...styles.existing }}>
            <pre style={styles.pre}>Your response is already captured</pre>
          </div>
        )}

        {!loading && !error && !isAlreadyApproved && (
          <div style={{ ...styles.messageBox, ...styles.success }}>
            <pre style={styles.pre}>Success! Your response is captured successfully</pre>
          </div>
        )}
      </section>
    </div>
  );
};

export default DataScriptApproval;
